import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { C, PageHeader, GlassCard, Input, Button, Badge, StatusDot, Sparkline, Skeleton, EmptyState } from "@coremonit/design-system";
import { Boxes, PlusCircle, Search, Filter, Tags, Users2, LayoutGrid, List as ListIcon } from "lucide-react";
import { useServices } from "../api/hooks";
import type { Service } from "@coremonit/types";

export function ServicesPage() {
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [env, setEnv] = useState(searchParams.get("env") ?? "");
  const [view, setView] = useState<"grid" | "list">("grid");
  const services = useServices({ q: q || undefined, status: status || undefined, env: env || undefined });
  const nav = useNavigate();

  useEffect(() => {
    setStatus(searchParams.get("status") ?? "");
    setEnv(searchParams.get("env") ?? "");
  }, [searchParams]);

  const items = services.data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<Boxes size={16} />}
        title="Services"
        subtitle={`${services.data?.total ?? 0} services · updated in real-time`}
        actions={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => nav("/services/new")}>Add Microservice</Button>}
      />

      {/* Filters */}
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input value={q} onChange={setQ} prefix={<Search size={13} />} placeholder="Search services, team, tag…" />
          </div>
          <FilterSelect value={status} onChange={setStatus} icon={<Filter size={12} />} label="All statuses"
            options={[
              { value: "", label: "All statuses" },
              { value: "healthy",  label: "Healthy" },
              { value: "warning",  label: "Warning" },
              { value: "critical", label: "Critical" },
              { value: "unknown",  label: "Unknown" },
            ]}
          />
          <FilterSelect value={env} onChange={setEnv} icon={<Tags size={12} />} label="All envs"
            options={[
              { value: "", label: "All envs" },
              { value: "production", label: "Production" },
              { value: "staging",    label: "Staging" },
              { value: "development",label: "Development" },
              { value: "uat",        label: "UAT" },
            ]}
          />
          <div style={{ display: "flex", gap: 2, padding: 2, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10 }}>
            <button
              onClick={() => setView("grid")}
              style={{ padding: "6px 8px", borderRadius: 8, background: view === "grid" ? C.blueDim : "transparent", color: view === "grid" ? C.blue : C.textMuted, border: "none", cursor: "pointer" }}
              title="Grid"
            ><LayoutGrid size={12} /></button>
            <button
              onClick={() => setView("list")}
              style={{ padding: "6px 8px", borderRadius: 8, background: view === "list" ? C.blueDim : "transparent", color: view === "list" ? C.blue : C.textMuted, border: "none", cursor: "pointer" }}
              title="List"
            ><ListIcon size={12} /></button>
          </div>
        </div>
      </GlassCard>

      {services.isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
          {Array.from({ length: 8 }, (_, i) => <Skeleton key={i} height={160} radius={16} />)}
        </div>
      ) : items.length === 0 ? (
        <GlassCard style={{ padding: 32 }}>
          <EmptyState icon={<Boxes size={22} />} title="No services match" description="Try clearing filters, or add a new microservice." action={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => nav("/services/new")}>Add Microservice</Button>} />
        </GlassCard>
      ) : view === "grid" ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
          {items.map((s) => <ServiceCard key={s.id} s={s} onOpen={() => nav(`/services/${s.id}`)} />)}
        </div>
      ) : (
        <ServicesTable items={items} onOpen={(s) => nav(`/services/${s.id}`)} />
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options, icon, label }: {
  value: string; onChange: (v: string) => void;
  options: { value: string; label: string }[];
  icon?: React.ReactNode; label: string;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, padding: "6px 8px" }}>
      {icon && <span style={{ color: C.textMuted }}>{icon}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ background: "transparent", border: "none", color: C.text, fontSize: 11, outline: "none", cursor: "pointer" }}
      >
        {options.map((o) => <option key={o.value} value={o.value} style={{ background: C.surface }}>{o.label}</option>)}
      </select>
    </div>
  );
}

function ServiceCard({ s, onOpen }: { s: Service; onOpen: () => void }) {
  const spark = useMemo(() => Array.from({ length: 20 }, () => s.latencyP95Ms * (0.6 + Math.random() * 0.7)), [s.id, s.latencyP95Ms]);
  const sColor = s.status === "critical" ? C.red : s.status === "warning" ? C.amber : s.status === "unknown" ? C.textMuted : C.green;
  return (
    <GlassCard onClick={onOpen} style={{ padding: 14 }} accent={sColor + "40"}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <StatusDot status={s.status} pulse={s.status !== "healthy"} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.name}</div>
            <div style={{ color: C.textMuted, fontSize: 10 }}>{s.language} · {s.team}</div>
          </div>
        </div>
        <Badge color={s.environment === "production" ? "blue" : s.environment === "staging" ? "amber" : "muted"}>{s.environment}</Badge>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
        <div>
          <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>p95 latency</div>
          <div style={{ color: sColor, fontSize: 16, fontWeight: 700 }}>{s.latencyP95Ms}ms</div>
        </div>
        <Sparkline data={spark} color={sColor} width={90} height={26} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 10 }}>
        <MiniKPI label="Error" value={`${s.errorRatePct.toFixed(2)}%`} color={s.errorRatePct > 1 ? C.red : C.green} />
        <MiniKPI label="RPM"   value={s.requestsPerMin.toLocaleString()} color={C.blue} />
        <MiniKPI label="Pods"  value={`${s.replicas.available}/${s.replicas.desired}`} color={s.replicas.available === s.replicas.desired ? C.green : C.amber} />
      </div>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 10 }}>
        {s.tags.map((t) => <Badge key={t} color="muted">{t}</Badge>)}
      </div>
    </GlassCard>
  );
}

function MiniKPI({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, padding: 6 }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 11, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function ServicesTable({ items, onOpen }: { items: Service[]; onOpen: (s: Service) => void }) {
  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 12 }}>
          <thead>
            <tr style={{ background: C.surfaceHigh, color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.4 }}>
              <Th>Service</Th><Th>Env</Th><Th>Team</Th><Th align="right">p95</Th><Th align="right">Errors</Th><Th align="right">RPM</Th><Th align="right">Pods</Th><Th align="right">Restarts</Th><Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} onClick={() => onOpen(s)} style={{ cursor: "pointer", borderTop: `1px solid ${C.border}` }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = C.surfaceHigh)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                <Td>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <StatusDot status={s.status} />
                    <span style={{ color: C.text, fontWeight: 600 }}>{s.name}</span>
                  </div>
                </Td>
                <Td><Badge color={s.environment === "production" ? "blue" : "muted"}>{s.environment}</Badge></Td>
                <Td>{s.team}</Td>
                <Td align="right"><span style={{ color: s.latencyP95Ms > 500 ? C.amber : C.textSub }}>{s.latencyP95Ms}ms</span></Td>
                <Td align="right"><span style={{ color: s.errorRatePct > 1 ? C.red : C.green }}>{s.errorRatePct.toFixed(2)}%</span></Td>
                <Td align="right">{s.requestsPerMin.toLocaleString()}</Td>
                <Td align="right">{s.replicas.available}/{s.replicas.desired}</Td>
                <Td align="right">{s.restarts24h}</Td>
                <Td><Badge color={s.status === "critical" ? "red" : s.status === "warning" ? "amber" : s.status === "unknown" ? "muted" : "green"}>{s.status}</Badge></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GlassCard>
  );
}

const Th = ({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) =>
  <th style={{ padding: "10px 14px", textAlign: align ?? "left", fontWeight: 700 }}>{children}</th>;
const Td = ({ children, align }: { children: React.ReactNode; align?: "left" | "right" }) =>
  <td style={{ padding: "10px 14px", textAlign: align ?? "left", color: C.textSub }}>{children}</td>;
